import { useEffect, useRef, useCallback } from 'react';
import { useActiveChargingStore } from '../store/activeChargingStore';
import { useAuthStore } from '../store/authStore';
import { liveService } from '../services/liveService';
import { Capacitor } from '@capacitor/core';
import { Haptics } from '@capacitor/haptics';

interface UseActiveChargingSessionOptions {
  onTransactionStopped?: (transactionId: string) => void;
}

export function useActiveChargingSession(options?: UseActiveChargingSessionOptions) {
  const { isAuthenticated } = useAuthStore();
  const {
    transactionId,
    hasActiveSession,
    fetchActiveSession,
    updateMeterValues,
    clearActiveSession,
  } = useActiveChargingStore();

  const onTransactionStoppedRef = useRef(options?.onTransactionStopped);
  useEffect(() => {
    onTransactionStoppedRef.current = options?.onTransactionStopped;
  }, [options?.onTransactionStopped]);

  useEffect(() => {
    if (!isAuthenticated) return;

    fetchActiveSession();

    if (!hasActiveSession) {
      const intervalId = window.setInterval(() => {
        fetchActiveSession();
      }, 30_000);
      return () => window.clearInterval(intervalId);
    }
  }, [isAuthenticated, hasActiveSession, fetchActiveSession]);

  const pollTransaction = useCallback(async () => {
    if (!transactionId) return;
    try {
      const tx = await liveService.getTransactionStatus(transactionId);

      updateMeterValues(tx.currentSoc, tx.currentPowerKw, tx.totalKwh);

      if (tx.status !== 'in_progress' && tx.status !== 'pending') {
        try {
          if (Capacitor.isNativePlatform()) {
            Haptics.vibrate();
          } else {
            navigator.vibrate?.(500);
          }
        } catch (e) {
          console.warn('[useActiveChargingSession] Vibration failed:', e);
        }

        onTransactionStoppedRef.current?.(transactionId);
        clearActiveSession();
      }
    } catch {
      // Poll failure — will retry next interval
    }
  }, [transactionId, updateMeterValues, clearActiveSession]);

  useEffect(() => {
    if (!transactionId || !isAuthenticated) return;

    pollTransaction();
    const intervalId = window.setInterval(pollTransaction, 30_000);

    return () => window.clearInterval(intervalId);
  }, [transactionId, isAuthenticated, pollTransaction]);

  return {
    hasActiveSession,
    transactionId,
    isConnected: hasActiveSession,
  };
}
